/**
 * 业务相关的类型
 */

import { HttpAPIError } from "@/api/model"
import {
  ERR_AGENT_OFFLINE,
  ERR_AGENT_REBOOT,
  ERR_CONN_TIMEOUT,
  ERR_DEV_STATUS_EXCEPTION,
  ERR_GET_DEVICE_API_REQ,
  ERR_GET_DEV_ID,
  ERR_DEV_API_REQ,
  ERR_GET_TOKEN_API_REQ,
  ERR_ICE,
  ERR_INVALID_KS_LOGIN_PWD,
  ERR_KICK_OUT,
  ERR_KS_LOGIN_EXPIRED,
  ERR_LOGIN_API_REQ,
  ERR_NO_DEVICE,
  ERR_NO_DISPLAYS,
  ERR_POWER_OFF,
  ERR_PENDING_CONNECTION,
  ERR_NOT_LOGIN,
  ERR_RTC_ERROR,
  ERR_NO_MEDIA_STREAMS,
  ERR_DEV_POWER_ON,
  ERR_DEV_LOCK,
  ERR_DEV_POWER_STOP_RESUME,
  ERR_SIGNALING_AUTH,
  ERR_SIGNALING_CONNECT_FAILED,
  SET_PASSWORD_TOKEN_EXPIRED,
  SET_PASSWORD_TOKEN_INVALID,
  SET_PASSWORD_PARAM_EMPTY,
  SET_PASSWORD_USER_NOT_FOUND,
  SET_PASSWORD_ALREADY_SET,
  ERR_KS_AUTH_CODE_EXPIRED,
  ERR_KS_AUTH_CODE_INVALID,
  ERR_DEV_POWER_RESTART,
  ERR_DEV_POWER_STOP,
  ERR_VERIFY_CODE_OR_PHONE_FAILED,
  ERR_CAPTCHA_VERIFY_API_REQ,
  ERR_CAPTCHA_NOT_EMPTY,
  ERR_LOGIN_TOO_MANY_TIMES,
  ERR_WEAK_PASSWORD,
  ERR_ACCOUNT_PASS_ERROR,
  ERR_GET_PUBLIC_KEY_API_REQ,
  ERR_SET_LOGIN_WALLPAPER,
  ERR_APPLY_DESKTOP_WALLPAPER,
  ERR_IMGCODE_API_REQ,
  ERR_SET_KC_PASSWORD_API_REQ,
  ERR_WALLPAPER_API_REQ,
  ERR_DEVICESECRET_API_REQ,
  ERR_DEV_STATUS_NOT_FOUND,
  ERR_KS_AUTH_CANCEL,
  ERR_NETWORK_NOT_RECOVER,
  ERR_OPEN_MIC,
  ERR_NO_MIC_PERMISSION,
  ERR_NO_CAMERA_PERMISSION,
  ERR_OPEN_CAMERA,
  ERR_CAMERA_UNMOUNT,
} from "./constants"

export type BizResult<T> = Ok<T> | BizError

export class Ok<T> {
  constructor(readonly value: T) {}
  unwrap(): T {
    return this.value
  }
}

// 业务代码层面的错误
export class BizError {
  // 业务错误码，如果是 HTTP API 错误，会沿用其错误码
  readonly code: number
  readonly msg: string
  readonly data: any

  constructor(code: number, msg?: string, data?: any) {
    this.code = code
    this.msg = msg || ""
    this.data = data
  }

  // 从 HTTP API 错误转换为业务错误
  static fromHttpError(e: HttpAPIError): BizError {
    return new BizError(e.code, e.msg, e.data)
  }

  get displayErrorMsg(): string {
    // return DISPLAYED_ERR_MSGS.get(this.code) || `未知错误: ${this.code}`
    return (
      DISPLAYED_ERR_MSGS.get(this.code) ||
      `${this.msg}` ||
      `未知错误: ${this.code}`
    )
  }
}
// 保留接口返回的错误码、错误信息
export class HttpBizError {
  // 业务错误码，如果是 HTTP API 错误，会沿用其错误码
  readonly code: number
  readonly msg: string
  readonly data: any

  constructor(code: number, msg: string, data: any) {
    this.code = code
    this.msg = msg
    this.data = data
  }

  // 保留 HTTP API 错误
  static stayHttpError(e: HttpAPIError): HttpBizError {
    return new HttpBizError(e.code, e.msg!, e.data)
  }

  get displayErrorMsg(): string {
    return `${this.msg}`
  }
}

// 云桌面设备信息
export interface RemoteDeskInfo {
  id: number
  desktopId: string
  name: string
  // 云桌面设备IP
  serverPrivateIp: string | null
  // Relay服务器IP
  gateWayIp: string | null
  // 云桌面公网出口 IP
  desktopPublicIp?: string | null
  // 云桌面所属机房 id
  idcId?: string | null
  // 云桌面设备状态
  status: DeviceStatus
  online?: string
  businessStatus?: string
  // 客户端是否允许发起重启
  canRestart?: boolean
}

export type DeviceStatus =
  | "Pending"
  | "Starting"
  | "Running"
  | "Stopping"
  | "Stopped"
  | "Restarting"
  | "Unknown"

export interface AudioDevice {
  index: number
  name: string
}

// 音频设备信息
export interface AudioDevicesInfo {
  // 音频输出设备
  outputs: AudioDevice[]
  // 音频输入设备
  inputs: AudioDevice[]
  // 输出音量
  outputVolume: number
  // 输入音量
  inputVolume: number
}

export interface VideoDevice {
  index: number
  name: string
}
// 视频设备信息
export interface VideoDevicesInfo {
  // 视频输出设备
  outputs: VideoDevice[]
  // 视频输入设备
  inputs: VideoDevice[]
  // 输出音量
  outputVolume: number
  // 输入音量
  inputVolume: number
}

export interface LoginParam {
  // 用户输入的用户名
  userName?: string

  // 用户输入的密码
  pwd: string

  // 手机号
  phone?: string

  // 短信验证码
  smsCode?: string

  // 短信验证码token
  smsToken?: string

  // 图片验证码ID
  captchaId?: string

  // 图片验证码
  captcha?: string
}

export type LoginType = "KS" | "Account" | "PC"
export interface User {
  account: string
  loginAccount?: string
  nickName: string
  avatar: string
  loginType: LoginType
  ksAuthCode?: string // 企业登录token,从授权url中获取
  uid?: string // 企业登录的用户ID,从授权url中获取(wangliping2这种,是邮箱前缀，主要用作密码校验)
  // 用于数据上报的字段
  accountID?: number
  business_scene?: string
  region?: string
  localDevId?: string
  desktopId?: string
  deviceId?: string
}
export interface KcLoginParam {
  user: User
  hasSetPass: number | null
}

export function userDisplayName(user: User) {
  if (user.loginType === "KS") {
    return user.nickName + "(企业)"
  }
  return user.nickName
}

// 用于 API 授权
export interface AuthHeader {
  token: string
  companyUniqID: string
}

const connErr1 = "连接云电脑服务器失败，请检查本地网络连接或联系IT管理员"

const DISPLAYED_ERR_MSGS = new Map<number, string>([
  // 登录
  [ERR_LOGIN_TOO_MANY_TIMES, "登录失败次数过多，请 5 分钟后再试"],
  [-10002, "您的账户已被永久锁定，请联系管理员解锁"],
  [-10003, "账号无效, 请联系IT管理员"],
  [-10004, "系统登录-未查找到公司ID"],
  [-10005, "账号无效, 请联系IT管理员"],
  [ERR_ACCOUNT_PASS_ERROR, "登录失败，请检查账号或密码"],
  [ERR_SET_LOGIN_WALLPAPER, "应用登录页壁纸失败"],
  [ERR_APPLY_DESKTOP_WALLPAPER, "应用云桌面壁纸失败"],
  [-10010, "系统错误：创建Token失败"],
  [-10011, "系统错误：缓存token失败设置登录状态失败"],
  [-10020, "系统错误：删除旧Token失败"],
  [ERR_LOGIN_API_REQ, "登录接口请求失败"],
  [ERR_DEVICESECRET_API_REQ, "获取 deviceSecret 接口请求失败"],
  [ERR_CAPTCHA_VERIFY_API_REQ, "图片验证码错误,请重新输入"],
  [ERR_CAPTCHA_NOT_EMPTY, "验证码不能为空,请输入验证码"],
  [-10141, "未分配云电脑实例"],
  [ERR_DEV_LOCK, "设备已被锁定，禁止登录"],
  [
    ERR_VERIFY_CODE_OR_PHONE_FAILED,
    "手机号或短信验证码校验失败，60秒后请重新发送验证码",
  ],

  [ERR_WEAK_PASSWORD, "您当前密码强度较弱，请点击【重置密码】进行修改"],

  [-10052, "您还未注册，请先注册再登录"],
  [-10053, "当前用户已被禁止登录，如有疑问请联系IT管理员"],
  [-10054, "WPS帐号认证-内部错误,Company数据库配置错误"],
  [-10055, "WPS帐号认证-JWT创建Token失败"],
  [-10056, "WPS帐号认证-授权页面配置异常"],
  [-10057, "WPS帐号认证-系统账号配置为空"],
  [ERR_INVALID_KS_LOGIN_PWD, "登录失败，密码错误"],
  [ERR_KS_LOGIN_EXPIRED, "企业授权过期，请重新登录授权"],
  [ERR_KS_AUTH_CODE_EXPIRED, "企业授权过期，请重新登录授权"],
  [ERR_KS_AUTH_CODE_INVALID, "企业授权过期，请重新登录授权"],
  [ERR_KS_AUTH_CANCEL, "已取消企业授权"],

  // 获取 local id
  [ERR_GET_DEV_ID, "获取本地设备ID接口请求失败"],

  // 获取机器列表
  [-10101, "系统错误-查询数据库异常"],
  [ERR_GET_DEVICE_API_REQ, "获取设备列表接口请求失败"],
  [-10103, "企业设备列表为空"],
  [ERR_NO_DEVICE, "当前账号未分配云电脑，请联系IT管理员"],

  // 获取机器状态
  [ERR_DEV_STATUS_NOT_FOUND, "设备列表为空"],
  // 获取设备状态
  [ERR_DEV_API_REQ, "获取设备状态接口请求失败"],
  // 控制台接口：开关机接口参数错误
  [-10125, "开关机接口参数错误"],
  [ERR_DEV_POWER_ON, "云电脑开机失败,请联系IT管理员"],
  [ERR_DEV_POWER_STOP_RESUME, "该机器不支持关机/重启，请联系IT管理员"], // desktopID以b开头: 移动云（禁用重启/关机功能）

  [ERR_DEV_POWER_STOP, "云电脑关机失败,请联系IT管理员"],
  [ERR_DEV_POWER_RESTART, "云电脑重启失败,请联系IT管理员"],
  [ERR_DEV_STATUS_EXCEPTION, "机器状态异常"],

  // 获取连接秘钥
  [-10150, connErr1],
  [-10151, "参数错误-OppDeviceId，MyDeviceId为空"],
  [-10152, "数据库用户id所绑定的deviceId与getToken参数中的OppDeviceId不匹配"],
  [ERR_GET_TOKEN_API_REQ, "获取连接设备token接口请求失败"],

  // 连接
  [ERR_AGENT_OFFLINE, "agent下线"],
  [-10214, "查询 Agent 在线状态接口失败"],
  [ERR_KICK_OUT, "您的云电脑在其他设备登录，您已被强制下线"],
  [ERR_AGENT_REBOOT, "您的云电脑正在重启，请稍后再尝试连接"],
  [ERR_NO_DISPLAYS, "获取不到显示器"],
  [ERR_ICE, "重连中..."],
  [ERR_CONN_TIMEOUT, "连接超时，请重试"],
  [ERR_POWER_OFF, "您的云电脑已关机，您已被强制下线"],
  [ERR_PENDING_CONNECTION, "正在连接中，请稍等..."],
  [ERR_NOT_LOGIN, "连接云电脑服务器失败，请刷新网页重试"],
  [ERR_RTC_ERROR, "云电脑内部错误，请重试"],
  [ERR_NO_MEDIA_STREAMS, "获取不到音视频流"],
  [ERR_SIGNALING_AUTH, "授权过期，请重新登录"],
  [ERR_SIGNALING_CONNECT_FAILED, "信令连接失败"],
  [ERR_NETWORK_NOT_RECOVER, connErr1],

  // 外设
  [ERR_OPEN_MIC, "麦克风无法打开"],
  [ERR_NO_MIC_PERMISSION, "无权限使用麦克风"],
  [ERR_NO_CAMERA_PERMISSION, "无权限使用摄像头"],
  [ERR_OPEN_CAMERA, "摄像头模组异常，尝试重启电脑恢复使用"],
  [ERR_CAMERA_UNMOUNT, "摄像头连接已断开"],

  // Token 权限校验
  [-10302, "用户授权已过期，请重新登录"],
  [-10301, "您的帐户异地登陆或令牌失效，请重新登录"],
  [-10300, "未登录或非法访问，请联系IT管理员"],
  [-10304, "请求参数错误，请联系IT管理员"],

  // 其余 API 错误
  [-9999, "点击次数过多，请稍后再试"],

  // 设置企业登录密码 接口错误码
  [SET_PASSWORD_TOKEN_EXPIRED, "企业授权过期，请重新登录授权"], // token已过期
  [SET_PASSWORD_TOKEN_INVALID, "企业授权过期，请重新登录授权"], // token无效
  [SET_PASSWORD_PARAM_EMPTY, "企业授权过期，请重新登录授权"], // token或密码为空
  [SET_PASSWORD_USER_NOT_FOUND, "账号无效, 请联系IT管理员"],
  [SET_PASSWORD_ALREADY_SET, "已设置过密码，请直接登录"],

  // 获取公钥 请求失败
  [ERR_GET_PUBLIC_KEY_API_REQ, "请求失败"],

  // 获取图片验证码
  [ERR_IMGCODE_API_REQ, "获取图片验证码失败"],

  // 设置企业登录密码失败
  [ERR_SET_KC_PASSWORD_API_REQ, "设置企业登录密码失败"],

  // 获取壁纸列表
  [ERR_WALLPAPER_API_REQ, "获取壁纸失败"],

  // -10664 获取图形验证码失败
  [-10664, "获取图形验证码失败"],

  // 验证图形验证码
  [-10665, "图片验证码错误"],

  // 发送短信验证码
  [-10666, "发送短信验证码失败"],

  // 获取最近登录设备
  [-10826, "获取已登录设备列表失败"],

  // 设置或取消可信设备
  [-10829, "设置或取消信任设备失败"],

  // 获取隐私配置
  [-10827, "获取隐私配置失败"],

  // 更新隐私配置
  [-10828, "更新隐私配置失败"],
])
